import { useEffect, useState } from "react";
import api, { formatDateTime, apiErrorMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export function revisaoAberta(ins, r) {
  return !!ins?.nf_precisa_revisao && !r.atendida_em;
}

export function RevisoesLista({ ins, soAbertas = false, testid = "revisoes-lista" }) {
  const lista = [...(ins?.nf_revisoes || [])].reverse().filter((r) => !soAbertas || revisaoAberta(ins, r));
  if (!lista.length) return null;
  return (
    <ol className="space-y-2" data-testid={testid}>
      {lista.map((r) => {
        const aberta = revisaoAberta(ins, r);
        return (
          <li key={r.id} className={`rounded-lg border p-2 text-sm ${aberta ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={`revisao-${r.id}`}>
            <div className="text-slate-800 whitespace-pre-wrap">“{r.observacao}”</div>
            <div className="text-[11px] text-slate-500 mt-1">{r.solicitante_nome} · {formatDateTime(r.solicitado_em)}</div>
            <div className={`text-[11px] font-bold mt-0.5 ${aberta ? "text-red-700" : "text-emerald-700"}`}>
              {aberta ? "Aguardando o Fiscal" : r.atendida_em ? `Atendida por ${r.atendida_por_nome || "Fiscal"} · ${formatDateTime(r.atendida_em)}` : "Atendida"}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function RevisaoNfModal({ ins, onClose, onDone }) {
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (ins) setObs(""); }, [ins]);
  const emitida = ins?.nf_status === "emitida";

  const enviar = async () => {
    if (obs.trim().length < 3) return toast.error("Descreva o que precisa ser revisado");
    setBusy(true);
    try {
      await api.post(`/fiscal/inscricoes/${ins.id}/solicitar-revisao?observacao=${encodeURIComponent(obs.trim())}`);
      toast.success("Solicitação registrada e enviada ao Fiscal");
      onDone?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro ao enviar"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!ins} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="modal-revisao-nf">
        <DialogHeader>
          <DialogTitle>{emitida ? "Solicitar revisão da NF" : "Enviar observação ao Fiscal"}</DialogTitle>
          <DialogDescription>{ins?.municipio_nome} · {ins?.curso_rotulo || ins?.curso_nome}</DialogDescription>
        </DialogHeader>
        <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={4}
          placeholder="Descreva o que precisa ser alterado (ex.: CNPJ, e-mail do responsável, valor, nomes dos alunos)"
          data-testid="revisao-nf-texto" />
        {ins?.nf_revisoes?.length > 0 && (
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Solicitações anteriores</div>
            <RevisoesLista ins={ins} testid="revisoes-anteriores" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={enviar} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="revisao-nf-enviar">
            {busy ? "Enviando..." : "Enviar ao Fiscal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
